// Persistent mini-player state — a single shared source of truth for the global
// FloatingVideoPlayer (mounted in App.vue, ABOVE the router) and the lesson that
// hands a playing video off to it on navigation. Reactive + sessionStorage-backed,
// so it survives both route changes AND an accidental page refresh (per tab).
import { reactive } from 'vue';

const KEY = 'youngpro.floatingPlayer';

const state = reactive({
  active: false, // is the mini-player showing?
  src: '', // video file url
  poster: '',
  title: '',
  lessonId: '', // the lesson to re-open on expand / hand back to
  currentTime: 0, // resume position, kept live as the mini-player plays
});

// Mirror the live state into sessionStorage so an accidental refresh restores the
// mini-player at the same spot (per-tab; cleared when the tab closes).
function persist() {
  try {
    if (state.active && state.src) {
      sessionStorage.setItem(KEY, JSON.stringify({
        src: state.src,
        poster: state.poster,
        title: state.title,
        lessonId: state.lessonId,
        currentTime: state.currentTime,
      }));
    } else {
      sessionStorage.removeItem(KEY);
    }
  } catch (e) {
    /* storage unavailable (private mode) — non-fatal */
  }
}

/** Start (or replace) the mini-player from a handed-off lesson video. */
function open({ src, poster, title, lessonId, currentTime } = {}) {
  if (!src) return;
  state.src = src;
  state.poster = poster || '';
  state.title = title || '';
  state.lessonId = lessonId || '';
  state.currentTime = currentTime || 0;
  state.active = true;
  persist();
}

// Keep the resume position live. The storage write is throttled (timeupdate fires
// several times a second); the in-memory currentTime stays exact regardless.
let lastPersist = 0;
function setTime(t) {
  if (typeof t !== 'number' || t < 0) return;
  state.currentTime = t;
  const now = Date.now();
  if (now - lastPersist > 2000) {
    lastPersist = now;
    persist();
  }
}

function close() {
  state.active = false;
  state.src = '';
  state.poster = '';
  state.title = '';
  state.lessonId = '';
  state.currentTime = 0;
  persist();
}

// On boot: restore a still-playing mini-player after an accidental refresh.
function restore() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return;
    const d = JSON.parse(raw);
    if (d && d.src) {
      state.src = d.src;
      state.poster = d.poster || '';
      state.title = d.title || '';
      state.lessonId = d.lessonId || '';
      state.currentTime = d.currentTime || 0;
      state.active = true;
    }
  } catch (e) {
    /* ignore malformed / unavailable storage */
  }
}
restore();

export const floatingPlayer = { state, open, setTime, close };
